<template>
    <div
        v-show="areVisible"
        ref="crumbs"
        class="w-full overflow-x-hidden bg-white pb-2 pt-5 print:hidden"
        :style="{ top: store.headerHeight + 1 + 'px' }"
    >
        <div
            v-show="route.name !== 'index'"
            class="relative mx-auto flex max-w-6xl flex-wrap items-center font-proregular 2xl:max-w-7xl"
        >
            <NuxtLink
                v-for="(page, index) in pages"
                :key="page.path"
                :to="localePath(page.path)"
                class="group shrink-0 pr-1 text-sm after:pl-1 after:text-black-text after:content-['/'] last:pr-0 last:after:content-none md:pr-2 md:after:pl-2"
                :class="
                    index < pages.length - 1 ||
                    isKnihaPage ||
                    isTemaPage ||
                    isKmecDistrictPage ||
                    isKmecMetrikaPage ||
                    isKimPage
                        ? 'text-weak'
                        : 'text-black-text'
                "
            >
                <span class="group-hover:text-black-text group-hover:underline">
                    {{
                        page.path === "/"
                            ? $t("general.crumbs_home")
                            : isDownloadPage
                              ? getDownloadPageTitle(page)
                              : isSearchPage
                                ? $t("search.title")
                                : store.getPageInfo(
                                        page.name
                                            ?.toString()
                                            .split("___")[0] as string
                                    )
                                  ? useLocaleValue(
                                        store.getPageInfo(
                                            page.name
                                                ?.toString()
                                                .split("___")[0] as string
                                        ),
                                        "title"
                                    )
                                  : page.meta.title
                    }}
                </span>
            </NuxtLink>

            <!-- Texty -->

            <NuxtLink
                v-if="isKnihaPage"
                class="pointer-events-none shrink-0 pr-1 text-sm text-black-text md:pr-2"
                :to="localePath(`/texty/${(route.params.kody as string)[0]}`)"
            >
                <span class="group-hover:text-black-text group-hover:underline">
                    {{
                        booksStore.books[locale as LanguageCode].find(
                            book =>
                                book.kod === (route.params.kody as string)[0]
                        )?.title
                    }}
                </span>
            </NuxtLink>

            <NuxtLink
                v-if="isTemaPage"
                class="group shrink-0 pr-1 text-sm text-weak after:pl-1 after:text-black-text after:content-['/'] md:pr-2 md:after:pl-2"
                :to="localePath(`/texty/${(route.params.kody as string)[0]}`)"
            >
                <span class="group-hover:text-black-text group-hover:underline">
                    {{
                        booksStore.books[locale as LanguageCode].find(
                            book =>
                                book.kod === (route.params.kody as string)[0]
                        )?.title
                    }}
                </span>
            </NuxtLink>
            <NuxtLink
                v-if="isTemaPage && Array.isArray(route.params.kody)"
                class="pointer-events-none shrink-0 pr-1 text-sm text-black-text md:pr-2"
                :to="
                    localePath(
                        `/texty/${route.params.kody[0]}/${route.params.kody[1]}`
                    )
                "
            >
                {{ getTemaNazev() }}
            </NuxtLink>

            <!-- Katalog městských částí -->

            <NuxtLink
                v-if="isKmecDistrictPage"
                class="pointer-events-none shrink-0 pr-1 text-sm text-black-text md:pr-2"
                :to="
                    localePath(
                        `/cisla/katalog-mestskych-casti/listy/${route.params.mestskaCastId as string}`
                    )
                "
            >
                {{ kmecStore.selectedDistrictName }}
            </NuxtLink>
            <NuxtLink
                v-if="isKmecMetrikaPage"
                class="pointer-events-none shrink-0 pr-1 text-sm text-black-text md:pr-2"
                :to="
                    localePath(
                        `/cisla/katalog-mestskych-casti/metriky/${route.params.metrikaId as string}`
                    )
                "
            >
                {{ kmecStore.metrikaTitle }}
            </NuxtLink>

            <!-- Katalog indikátorů a metrik -->

            <NuxtLink
                v-if="isKimPage"
                class="pointer-events-none shrink-0 pr-1 text-sm text-black-text md:pr-2"
                :to="
                    localePath(
                        `/cisla/katalog-indikatoru-a-metrik/${route.params.skupinaMetrikKod as string}`
                    )
                "
            >
                {{ useLocaleValue(kimStore.selectedSkupinaMetrik, "NAZEV") }}
            </NuxtLink>

            <!-- <NuxtLink
                v-if="isDownloadPage"
                class="pointer-events-none pl-2 text-sm shrink-0 text-black-text"
                :to="`/cisla/ke-stazeni`"
            >
                {{ route.name?.toString().split("-")[2].split("___")[0] }}
                {{
                    !route.name?.toString().split("-")[3]
                        ? ""
                        : route.name?.toString().split("-")[3] === "graficka"
                          ? "- grafická část"
                          : route.name?.toString().split("-")[3] === "textova"
                            ? "- textová část"
                            : "- přílohy"
                }}
            </NuxtLink> -->
        </div>
    </div>
</template>

<script setup lang="ts">
import type {
    RouteRecordNormalized,
    RouteLocationNormalizedLoaded,
    RouteLocationNormalizedLoadedGeneric,
} from "vue-router"

const localePath = useLocalePath()
const { locale, t } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useMainStore()
const booksStore = useBooksStore()
const kmecStore = useKmecStore()
const kimStore = useKimStore()

const crumbs = ref<HTMLElement | null>(null)

defineExpose({ crumbs })

const emit = defineEmits(["ready"])

onMounted(() => {
    emit("ready")
})

const areVisible = computed(() => {
    if (store.mobileMode && store.mobileLeftPanelOpen) {
        return false
    }
    if (route.path === "/" || route.path === "/en") {
        return false
    }
    return true
})

const pages = computed(() => {
    const array: Array<RouteRecordNormalized | RouteLocationNormalizedLoaded> =
        []
    const pagePaths = []
    const pagePathSegments = route.path.split("/")
    if (pagePathSegments[pagePathSegments.length - 1] === "") {
        pagePathSegments.pop()
    }
    for (let i = 0; i < pagePathSegments.length; i++) {
        if (i === 0) {
            pagePaths.push("/")
        } else {
            if (pagePathSegments[i] === "en") {
                continue
            }
            const path = route.path.split("/", i + 1).join("/")
            pagePaths.push(path)
        }
    }
    const possibleRoutes = router.getRoutes()
    pagePaths.forEach(item => {
        const page = possibleRoutes.find(el => el.path === item)
        if (page !== undefined) {
            array.push(page)
        }
    })
    if (isKmecDistrictPage.value || isKmecMetrikaPage.value) {
        array.splice(-1)
    }
    return array
})

const isKnihaPage = computed(() => {
    return (
        (route.name as string)?.includes("texty-kody") &&
        route.params.kody?.length === 1
    )
})

const isTemaPage = computed(() => {
    return (
        (route.name as string)?.includes("texty-kody") &&
        Array.isArray(route.params.kody) &&
        route.params.kody?.length > 1
    )
})

const isKmecDistrictPage = computed(() => {
    return (
        (route.name as string)?.includes("listy-mestskaCastId") &&
        route.params.mestskaCastId
    )
})

const isKmecMetrikaPage = computed(() => {
    return (
        (route.name as string)?.includes("metriky-metrikaId") &&
        route.params.metrikaId
    )
})

const isKimPage = computed(() => {
    return (
        (route.name as string)?.includes("metrik-skupinaMetrikKod") &&
        route.params.skupinaMetrikKod
    )
})

const isDownloadPage = computed(() => {
    return (route.name as string)?.includes("ke-stazeni-")
})

const isSearchPage = computed(() => {
    return (route.name as string)?.includes("vyhledavani")
})

function getDownloadPageTitle(
    page: RouteRecordNormalized | RouteLocationNormalizedLoadedGeneric
) {
    if (!isDownloadPage.value) return ""
    if (page.name?.toString().includes("20"))
        return page.path.substring(page.path.lastIndexOf("/") + 1)
    if (typeof page.meta.title !== "string") return t("download.title")
    if (page.meta.title.includes(" - ")) return page.meta.title.split(" - ")[1]
    return t("download.title")
}

function getTemaNazev() {
    if (!isTemaPage.value) return ""
    if (!Array.isArray(route.params.kody)) return ""
    const temaMetadata =
        booksStore.temaData[locale.value as LanguageCode][
            route.params.kody.join(".")
        ]?.METADATA_TEMA?.[0]
    if (!temaMetadata && route.params.kody[0] && route.params.kody[1]) {
        return booksStore.bookTrees[locale.value as LanguageCode][
            route.params.kody[0]
        ]?.find(item => item.kod === route.params.kody?.[1])?.nazev
    }
    return booksStore.temaData[locale.value as LanguageCode][
        route.params.kody.join(".")
    ]?.METADATA_TEMA?.[0]?.NAZEV
}
</script>

<style lang="scss" scoped>
.bread-crumbs {
    @apply absolute -top-10 flex items-center justify-start sm:absolute sm:-top-12;

    .item {
        @apply mr-1 rounded-full bg-white py-1 text-xs font-medium text-gray-400 duration-150 last:font-bold hover:text-gray-800;

        &:before {
            @apply mr-1 content-["/"];
        }

        /* &:last-child {
            @apply pointer-events-none text-gray-800 font-bold;
        } */
        &.home {
            &:before {
                @apply content-none;
            }
        }
    }
}
</style>
